import { useCallback, useState } from 'react';

import type { ErrorMessage } from 'src/components/PreflightProvider/PreflightProvider';
import { apiClient, onApiRequest } from 'src/modules/api';
import {
  redirectWithCode,
  redirectWithError,
} from 'src/pages/ConsentPage/redirect';
import type { AcceptAction, RejectAction } from 'src/types/api';

import { usePreflight } from '../components/PreflightProvider';

export const useUserActions = () => {
  const {
    request,
    preflight: { sessionId, redirectUrl },
    state,
  } = usePreflight();

  const [loading, setLoading] = useState<{ accept: boolean; reject: boolean }>({
    accept: false,
    reject: false,
  });
  const [apiError, setApiError] = useState<ErrorMessage>();

  const onError = useCallback((_err: unknown) => {
    setApiError('Please try again later.');
    setLoading({ accept: false, reject: false });
  }, []);

  const onAccept = useCallback(
    async ({
      action,
      candidateWillingToShareProfile,
      shareableProfileExperiment,
    }: {
      action: AcceptAction;
      candidateWillingToShareProfile: boolean;
      shareableProfileExperiment: boolean;
    }) => {
      setLoading({ reject: loading.reject, accept: true });
      const onSuccess = async () => {
        const { code } = await apiClient.accept({
          ...request,
          sessionId,
        });
        apiClient.publish({
          ...request,
          sessionId,
          action,
          candidateWillingToShareProfile,
          candidateInExperiment: shareableProfileExperiment,
        });

        redirectWithCode({ state, redirectUrl }, code);
      };

      await onApiRequest(onSuccess, onError);
    },
    [loading.reject, onError, request, sessionId, state, redirectUrl],
  );

  const onReject = useCallback(
    async ({
      action,
      candidateWillingToShareProfile,
      shareableProfileExperiment,
    }: {
      action: RejectAction;
      candidateWillingToShareProfile: boolean;
      shareableProfileExperiment: boolean;
    }) => {
      setLoading({ accept: loading.accept, reject: true });

      const onSuccess = async () => {
        apiClient.publish({
          ...request,
          sessionId,
          action,
          candidateWillingToShareProfile,
          candidateInExperiment: shareableProfileExperiment,
        });
        redirectWithError({ state, redirectUrl }, 'access_denied');
      };

      await onApiRequest(onSuccess, onError);
    },
    [loading.accept, onError, request, sessionId, state, redirectUrl],
  );

  return {
    onAccept,
    onReject,
    loading,
    apiError,
  };
};
